"use client"
import {
  createContext,
  useContext,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react"
import { ArrowUpRight, MousePointerClick } from "lucide-react"
import { formatCompact } from "../../../plan-a/economic-data/calculations"
import {
  getHoveredRegionId,
  type HoveredPowerItem,
} from "../../../power-bubble-chart/hoveredPowerItem"
import {
  type PowerLabData,
  type PowerRegionData,
} from "../../../power-bubble-chart/powerBubbleChartData"
import { getLabsWorthShowing } from "../../../power-bubble-chart/distribution-of-power-tooltip/powerTooltipData"
import { usePowerRegions } from "./distribution-of-power-bubbles/usePowerRegions"
import {
  HoverSwapFadeOverlay,
  hoverSwapHiddenAndUntargetableContentStyle,
  hoverSwapHiddenContentStyle,
  useHoverSwapSequentialFade,
} from "../hoverSwapFade"
import { useHoverSwapsDisabled } from "../hoverSwapsDisabled"

// ─── Editable content ────────────────────────────────────────────────────────
// Type treatment for the in-canvas distribution-of-power takeover.
export const POWER_HOVER_STYLE = {
  headerFontSize: "11px",
  rowFontSize: "8.5px",
  rowLineHeight: 1.5,
  hoveredColor: "var(--accent)",
  // The "value-units" metric-label mode: size of the unit suffix riding each
  // region total ("246M H100e") — smaller than the number so it reads as a
  // unit, not part of the figure.
  totalSuffixFontSize: "8px",
}

// Title line above the takeover's region columns, naming what the hover is
// showing (same wording as the click-open panel's title).
export const POWER_HOVER_TITLE = "Distribution of Power"
export const POWER_HOVER_TITLE_FONT_SIZE = "11px"

// The click affordance shown in the hover takeover (tabs 13–15 try three
// treatments — see PowerHoverClickHint below).
export const POWER_HOVER_CLICK_HINT_LABEL = "Click for details"

// Emphasis treatments for the hovered region's group in the below-HUD
// breakdown, matching the treemap's stronger red highlight (one tab each):
//   "chip"      — the region header inverts into a solid red chip, cream text
//   "frame"     — the whole column takes a red rounded outline
//   "rail"      — a thick red rule down the column's left edge, red header
//   "underline" — a red rule UNDER the header line, full column width
//   "overline"  — the same rule ABOVE the header instead (tops the column)
//   "marker"    — a small red square before the region name (echoing the
//                 treemap's cell shape), text stays black
// In every mode the OTHER groups stay full black (no opacity dimming).
export type PowerBreakdownRegionEmphasis =
  | "chip"
  | "frame"
  | "rail"
  | "underline"
  | "overline"
  | "marker"

// How the below-HUD breakdown names its METRIC (H100-equivalent compute) —
// dropped vs. the old floating tooltip, one tab per approach. Copy lives in
// POWER_METRIC_COPY (belowHudDetailSwap.tsx), next to where it renders:
//   "units-header" — an old-tooltip-style "Name … H100 equiv." column-header
//                    row between the title and the region columns
//   "title-units"  — small "in H100 equivalents" riding the title line's
//                    right edge
//   "definition"   — a sans one-liner under the title (the capability
//                    ladder's title+subtitle voice)
//   "value-units"  — each region total carries an "H100e" unit suffix
//   "footnote"     — a tiny mono footer line under the columns
//   Typographic takes on the "definition" sentence (base: tab 11):
//   "definition-inline"  — right-aligned beside the title, wrapping to a
//                          narrow right-aligned block on the title line
//   "definition-italic"  — under the title, italicized (the old floating
//                          tooltip's parenthetical voice)
//   "definition-measure" — under the title, capped to a comfortable reading
//                          measure instead of running the full HUD width
//   "definition-nowrap"  — under the title on a single line that never
//                          wraps ("definition-measure" sans the line break)
//   "measured-inline"    — the short "Measured in …" line in the
//                          definition-inline position (title line, right)
//   "measured-below"     — the short "Measured in …" line under the title
//                          (the "definition" position with the short copy)
export type PowerBreakdownMetricLabel =
  | "units-header"
  | "title-units"
  | "definition"
  | "definition-inline"
  | "definition-italic"
  | "definition-measure"
  | "definition-nowrap"
  | "measured-inline"
  | "measured-below"
  | "value-units"
  | "footnote"
// How the below-HUD breakdown SEGMENTS its holders when split by FRONTIER
// ACCESS instead of geography (the 2026-07-12 exploration): title, subtitle,
// then a "companies with access to frontier models" segment and an "Others"
// segment. One design per tab; the copy/knobs and the component live in
// belowHudDetailSwap.tsx (POWER_FRONTIER_SPLIT_COPY / _STYLE):
//   "stacked"        — the two segments as full-width bands, rows flowing in
//                      columns inside each band
//   "side-by-side"   — frontier left, others right, one column each
//   "red-rail"       — stacked, the frontier segment marked with the thick
//                      red left rail (the DC-hover / power rail language)
//   "region-tags"    — stacked, every row keeps a small geography tag so the
//                      old region columns' information survives
//   "segment-totals" — stacked, each segment header carries its summed
//                      compute right-aligned (the region headers' language)
//   "by-country"     — BOTH dimensions: the frontier/others bands each hold
//                      the three country columns (region sub-header with
//                      that region's segment total, then its rows)
//   "by-country-outline"     — by-country, each segment BAND boxed in the
//                              treemap cells' outline vocabulary: the
//                              frontier band outlined red, Others black
//                              (POWER_FRONTIER_SPLIT_STYLE.segmentOutline)
//   "by-country-row-outline" — by-country, every company ROW in its own
//                              small box — frontier companies' boxes red,
//                              the others' black
//                              (POWER_FRONTIER_SPLIT_STYLE.rowOutline)
//   "by-country-row-marker"  — by-country, a small outline box BEFORE each
//                              row's name (the treemap cell in miniature) —
//                              red 2px for frontier companies, black 1px
//                              for the others
//                              (POWER_FRONTIER_SPLIT_STYLE.rowLeadingBox)
//   "by-country-outline-top-labels" — tab 25's two outlined bands, but each
//                              country labeled ONCE: the sub-headers show in
//                              the frontier band only; the Others band's
//                              columns align underneath, unlabeled
//   "by-country-tl-*" — tab 31's lineage ("tl" = top-labels): each design
//                              adds one way of making the Others rows read
//                              as part of the SAME column (knobs in the
//                              POWER_FRONTIER_SPLIT_STYLE.tl* dicts,
//                              belowHudDetailSwap.tsx):
//     "-dividers"       — hairline vertical rules between the columns,
//                         running through both bands (continuous tracks)
//     "-whisper-labels" — the Others band repeats each country name, tiny
//                         (de-emphasis by size, no total)
//     "-labels-outside" — the country headers move ABOVE the red box,
//                         owning the whole column (name + full total)
//     "-hover-rail"     — hovering a country marks ITS column across both
//                         bands with a red left rail (space reserved)
//     "-rule"           — no box: a red rule under the frontier band
//                         delimits it, so columns read continuous
//   "country-columns-*" — the country-first restructure (tabs 28–30, 32):
//                              three columns, one per country, each labeled
//                              once at the top; the frontier companies are
//                              the column's TOP group, still called out as
//                              frontier access — by a red outline box
//                              ("-box"), a thick red left rail ("-rail"),
//                              or per-group text labels ("-headers");
//                              "-headers-hover" additionally reds the
//                              hovered country's header in the pane. Copy
//                              and knobs in POWER_COUNTRY_COLUMNS_COPY /
//                              _STYLE (belowHudDetailSwap.tsx).
//   "country-columns-rail-key-right" — the rail design with the key moved
//                              to the TITLE LINE's right edge (no standalone
//                              legend line above the columns)
//   "country-columns-rail-key-right-icons" — + every frontier row carries
//                              its company icon (the treemap's frontier
//                              mark, brought into the list), and the key
//                              shows the icon as part of its glyph
//   "country-columns-rail-key-right-hover" — the key-right rail design plus
//                              the hovered country's header turning red in
//                              the pane (no underline)
//   "country-columns-icons-underline" — the icons design with NO rail: the
//                              red icons alone mark frontier access (icon-
//                              only key, right-aligned), and the hovered
//                              country shows via the tab-0 horizontal line —
//                              a red rule under its column header, header
//                              red (POWER_COUNTRY_COLUMNS_STYLE
//                              .hoveredUnderline)
export type PowerFrontierSplitDesign =
  | "stacked"
  | "side-by-side"
  | "red-rail"
  | "region-tags"
  | "segment-totals"
  | "by-country"
  | "by-country-outline"
  | "by-country-row-outline"
  | "by-country-row-marker"
  | "by-country-outline-top-labels"
  | "by-country-tl-dividers"
  | "by-country-tl-whisper-labels"
  | "by-country-tl-labels-outside"
  | "by-country-tl-hover-rail"
  | "by-country-tl-rule"
  | "country-columns-box"
  | "country-columns-rail"
  | "country-columns-headers"
  | "country-columns-headers-hover"
  | "country-columns-rail-key-right"
  | "country-columns-rail-key-right-icons"
  | "country-columns-rail-key-right-hover"
  | "country-columns-icons-underline"
  // The icons-underline design with the key UNDER the measured-in subheader
  // (a standalone line above the columns) instead of on the title line's
  // right edge.
  | "country-columns-icons-underline-key-below"
  // The icons-underline design with the key in the SUBTITLE's voice — same
  // font size and weight as the measured-in line, sharing the subtitle's
  // row (key right). Knob in POWER_COUNTRY_COLUMNS_STYLE.keySubtitleVoice.
  | "country-columns-icons-underline-key-subtitle"
  // The same subtitle-voice row with the two SWAPPED: key left, the
  // measured-in line right.
  | "country-columns-icons-underline-key-subtitle-swapped"
  // The subtitle-voice key STACKED under the measured-in line instead of
  // sharing its row — title, subtitle, then the key line.
  | "country-columns-icons-underline-key-subtitle-stacked"

// The capability-takeover-style hierarchy for the breakdown
// (breakdownHierarchy on PowerHoverSwap): bigger title, region headers at
// BODY size with bold carrying the emphasis (still mono, i.e. Menlo/SF Mono),
// and the lab rows in the sans body voice — the same face as the "Who holds
// the…" definition line (font-sans = the Apple system font on macOS).
export const POWER_BREAKDOWN_HIERARCHY_STYLE = {
  titleFontSize: "12px",
  // Headers and rows both sit at the definition line's size
  // (POWER_METRIC_LABEL_STYLE.definitionFontSize) — headers are just bolded.
  regionHeaderFontSize: "9.5px",
  rowFontSize: "9.5px",
}

export const POWER_BREAKDOWN_EMPHASIS_STYLE = {
  chip: {
    background: "var(--accent)",
    textColor: "#fffff8",
    // Negative margins cancel the padding so the header text stays aligned
    // with the rows below whether or not the chip is showing.
    padding: "1px 4px",
    margin: "-1px -4px",
    borderRadiusPx: 2,
  },
  frame: {
    borderWidthPx: 1.5,
    borderColor: "var(--accent)",
    paddingPx: 4,
    borderRadiusPx: 3,
  },
  rail: {
    railWidthPx: 3,
    railColor: "var(--accent)",
    gapPx: 6,
    headerColor: "var(--accent)",
  },
  underline: {
    ruleWidthPx: 2,
    ruleColor: "var(--accent)",
    // Gap between the header's baseline and the rule.
    gapPx: 2,
  },
  overline: {
    ruleWidthPx: 2,
    ruleColor: "var(--accent)",
    // Gap between the rule and the header's top edge.
    gapPx: 3,
  },
  marker: {
    squareSizePx: 7,
    squareColor: "var(--accent)",
    // Matches the treemap cells' rounding so the marker reads as one of them.
    squareRadiusPx: 2,
    gapPx: 5,
  },
}

// The combo layout caps each lab list so it can't run past the bottom of the
// HUD's other visuals; labs beyond the cap collapse into an "And N more"
// line. The US/China columns each get this many rows; the Rest-of-World block
// under the treemap flows across two columns, so it gets twice as many before
// capping.
export const POWER_COMBO_MAX_LAB_ROWS = 7
export const POWER_COMBO_MORE_LABEL = (count: number) => `And ${count} more`

// Rest of World stacks in the China column — until the combined column
// (2 headers + China's rows + RoW's rows) exceeds this many rows, at which
// point RoW scoots over to the right side, under the dist-of-power section
// (PowerHoverWorldBelowTreemap's onlyWhenWorldScootsRight).
export const POWER_COMBO_STACKED_MAX_ROWS = 9

type PowerHoverSwapContextValue = {
  hoveredItem: HoveredPowerItem | null
  // The raw state setter (functional updates allowed): ClaimPowerHoverOnEnter
  // claims the whole-chart hover as `prev => prev ?? {kind:"chart"}` so a
  // cell hover landing in the same pointer gesture can never be clobbered by
  // the claim reading a stale null (see hoverSwapStickyZone.tsx).
  setHoveredItem: Dispatch<SetStateAction<HoveredPowerItem | null>>
  breakdownRegionEmphasis?: PowerBreakdownRegionEmphasis
  breakdownMetricLabel?: PowerBreakdownMetricLabel
  // Drops the per-ITEM highlights in the breakdown (the red-bold hovered lab
  // row, the red region header) — the column-level emphasis alone shows
  // where the pointer is.
  breakdownItemHighlightDisabled?: boolean
  // The capability-takeover-style hierarchy — see
  // POWER_BREAKDOWN_HIERARCHY_STYLE.
  breakdownHierarchy?: boolean
  // Split the breakdown by frontier access instead of geography — see
  // PowerFrontierSplitDesign above. Unset keeps the region columns.
  breakdownFrontierSplit?: PowerFrontierSplitDesign
}

const PowerHoverSwapContext = createContext<PowerHoverSwapContextValue | null>(null)

// Null outside a <PowerHoverSwap>. DistributionOfPowerBubbles4 reads this to
// decide whether hovering the treemap should open its floating tooltip (no
// provider) or instead report here so a PowerHoverSwapTarget can render the
// distribution-of-power info on the HUD canvas.
export const usePowerHoverSwap = () => useContext(PowerHoverSwapContext)

// Renders no DOM of its own, so wrapping existing flex/grid children does not
// disturb their layout relationship to the parent container.
// breakdownRegionEmphasis picks the hovered-region treatment the below-HUD
// breakdown renders with (POWER_BREAKDOWN_EMPHASIS_STYLE);
// breakdownMetricLabel picks how it names its metric (POWER_METRIC_COPY).
export const PowerHoverSwap = ({
  children,
  breakdownRegionEmphasis,
  breakdownMetricLabel,
  breakdownItemHighlightDisabled,
  breakdownHierarchy,
  breakdownFrontierSplit,
}: {
  children: ReactNode
  breakdownRegionEmphasis?: PowerBreakdownRegionEmphasis
  breakdownMetricLabel?: PowerBreakdownMetricLabel
  breakdownItemHighlightDisabled?: boolean
  breakdownHierarchy?: boolean
  breakdownFrontierSplit?: PowerFrontierSplitDesign
}) => {
  const [hoveredItem, setHoveredItem] = useState<HoveredPowerItem | null>(null)
  // Hover is not an input on this surface (mobile dashboard) — provide
  // null so every consumer renders its base, unhovered content.
  const hoverSwapsDisabled = useHoverSwapsDisabled()
  return (
    <PowerHoverSwapContext.Provider
      value={hoverSwapsDisabled ? null : {
        hoveredItem,
        setHoveredItem,
        breakdownRegionEmphasis,
        breakdownMetricLabel,
        breakdownItemHighlightDisabled,
        breakdownHierarchy,
        breakdownFrontierSplit,
      }}
    >
      {children}
    </PowerHoverSwapContext.Provider>
  )
}

// Region header line: region name, right-aligned compute total. No flag icon,
// and the name line-wraps rather than truncating — both so long names like
// "Rest of World" always read in full in the narrow combo columns. Exported
// for the side-by-side hover strip (powerHoverSideBySide.tsx).
export const RegionHeaderLine = ({
  region,
  highlighted,
  // Overrides the header color outright (the emphasis treatments use it:
  // cream on the chip's red fill, red for the rail's header).
  colorOverride,
  // The "marker" emphasis: a small red square before the region name
  // (POWER_BREAKDOWN_EMPHASIS_STYLE.marker). Present on EVERY column in
  // marker mode — transparent when not hovered — so names never shift.
  leadingMarker,
  // The "value-units" metric label: unit suffix after the total ("H100e"),
  // rendered smaller than the number (POWER_HOVER_STYLE.totalSuffixFontSize).
  totalSuffix,
  // The capability-voice hierarchy: header drops to BODY size, bold (still
  // mono) carrying the emphasis — see POWER_BREAKDOWN_HIERARCHY_STYLE.
  hierarchy = false,
}: {
  region: PowerRegionData
  highlighted: boolean
  colorOverride?: string
  leadingMarker?: { visible: boolean }
  totalSuffix?: string
  hierarchy?: boolean
}) => {
  return (
    <div
      className="flex items-baseline justify-between gap-2 font-mono font-bold"
      style={{
        fontSize: hierarchy
          ? POWER_BREAKDOWN_HIERARCHY_STYLE.regionHeaderFontSize
          : POWER_HOVER_STYLE.headerFontSize,
        color: colorOverride ?? (highlighted ? POWER_HOVER_STYLE.hoveredColor : undefined),
      }}
    >
      <span className="flex min-w-0 items-center">
        {leadingMarker && (
          <span
            className="shrink-0"
            style={{
              width: POWER_BREAKDOWN_EMPHASIS_STYLE.marker.squareSizePx,
              height: POWER_BREAKDOWN_EMPHASIS_STYLE.marker.squareSizePx,
              borderRadius: POWER_BREAKDOWN_EMPHASIS_STYLE.marker.squareRadiusPx,
              background: leadingMarker.visible
                ? POWER_BREAKDOWN_EMPHASIS_STYLE.marker.squareColor
                : "transparent",
              marginRight: POWER_BREAKDOWN_EMPHASIS_STYLE.marker.gapPx,
              transition: "background 160ms ease",
            }}
          />
        )}
        <span className="min-w-0">{region.name}</span>
      </span>
      <span className="whitespace-nowrap">
        {formatCompact(region.computeStock)}
        {totalSuffix && (
          <span
            className="font-normal"
            style={{ fontSize: POWER_HOVER_STYLE.totalSuffixFontSize }}
          >
            {" "}
            {totalSuffix}
          </span>
        )}
      </span>
    </div>
  )
}

// Small outlined box around each lab row (the frontier split's
// "by-country-row-outline" design, echoing the treemap cells' outline
// vocabulary). The literal knobs live in POWER_FRONTIER_SPLIT_STYLE.rowOutline
// (belowHudDetailSwap.tsx) — the render site there passes them through.
export type LabRowBox = {
  borderWidthPx: number
  borderColor: string
  paddingX: number
  paddingY: number
  borderRadiusPx: number
  // Vertical gap between consecutive boxed rows.
  rowGapPx: number
}

// Small outline box BEFORE a lab row's name (the frontier split's
// "by-country-row-marker" design — a treemap cell in miniature). The literal
// knobs live in POWER_FRONTIER_SPLIT_STYLE.rowLeadingBox
// (belowHudDetailSwap.tsx) — the render site there passes them through.
export type LabRowLeadingBox = {
  squareSizePx: number
  borderWidthPx: number
  borderColor: string
  borderRadiusPx: number
  // Gap between the box and the name.
  gapPx: number
}

// One lab row: name left, compute right, accent when hovered. sansBody (the
// capability-voice hierarchy) sets the rows in the sans body face instead of
// mono — tabular-nums keeps the figures aligned without the mono grid.
const LabRowLine = ({
  name,
  compute,
  highlighted,
  sansBody = false,
  box,
  leadingBox,
  isFirstRow = false,
}: {
  name: ReactNode
  compute: number
  highlighted: boolean
  sansBody?: boolean
  // See LabRowBox — unset renders the plain unboxed row.
  box?: LabRowBox
  // See LabRowLeadingBox — unset renders the name with no leading mark.
  leadingBox?: LabRowLeadingBox
  // Boxed rows only: suppresses the inter-row gap above the first row.
  isFirstRow?: boolean
}) => (
  <div
    // Probe for the viewport row cap (useHoverDetailViewportRowCap): the
    // first lab row supplies the rows' top edge and per-row height.
    data-power-lab-row=""
    className={`flex items-baseline justify-between gap-2 ${sansBody ? "font-sans" : "font-mono"}`}
    style={{
      fontSize: sansBody
        ? POWER_BREAKDOWN_HIERARCHY_STYLE.rowFontSize
        : POWER_HOVER_STYLE.rowFontSize,
      lineHeight: POWER_HOVER_STYLE.rowLineHeight,
      fontWeight: highlighted ? 700 : 400,
      color: highlighted ? POWER_HOVER_STYLE.hoveredColor : undefined,
      ...(box && {
        border: `${box.borderWidthPx}px solid ${box.borderColor}`,
        borderRadius: box.borderRadiusPx,
        padding: `${box.paddingY}px ${box.paddingX}px`,
        marginTop: isFirstRow ? 0 : box.rowGapPx,
        boxSizing: "border-box" as const,
      }),
    }}
  >
    <span className="flex min-w-0 items-center">
      {leadingBox && (
        <span
          className="shrink-0"
          style={{
            width: leadingBox.squareSizePx,
            height: leadingBox.squareSizePx,
            border: `${leadingBox.borderWidthPx}px solid ${leadingBox.borderColor}`,
            borderRadius: leadingBox.borderRadiusPx,
            marginRight: leadingBox.gapPx,
            boxSizing: "border-box",
          }}
        />
      )}
      <span className="whitespace-nowrap overflow-hidden text-ellipsis">{name}</span>
    </span>
    <span className="whitespace-nowrap tabular-nums">{formatCompact(compute)}</span>
  </div>
)

// Rows a capped list actually paints (labs shown plus the "And N more" line),
// mirroring CappedLabList's only-truncate-when-it-saves-a-row rule.
const countShownListRows = (labCount: number, maxRows: number) =>
  labCount > maxRows + 1 ? maxRows + 1 : labCount

// The shared decision, used by BOTH the left overlay (whether to stack RoW
// under China) and PowerHoverWorldBelowTreemap (whether to render on the
// right instead) so the two can never both show it.
const shouldWorldScootRight = (regions: PowerRegionData[]) => {
  const china = regions.find((r) => r.id === "china")
  const world = regions.find((r) => r.id === "world")
  if (!china || !world) return false
  const chinaRows = countShownListRows(getLabsWorthShowing(china).length, POWER_COMBO_MAX_LAB_ROWS)
  const worldRows = countShownListRows(getLabsWorthShowing(world).length, POWER_COMBO_MAX_LAB_ROWS)
  return 2 + chinaRows + worldRows > POWER_COMBO_STACKED_MAX_ROWS
}

// Lab rows capped at maxRows: anything beyond collapses into a dim
// "And N more" line so the list can't outgrow its slot. Exported for the
// frontier-split breakdown (belowHudDetailSwap.tsx).
export const CappedLabList = ({
  labs,
  hoveredItem,
  maxRows,
  sansBody = false,
  renderName,
  rowBox,
  rowLeadingBox,
}: {
  labs: ReturnType<typeof getLabsWorthShowing>
  // Null renders the list with nothing highlighted (the panel redesigns'
  // static breakdown).
  hoveredItem: HoveredPowerItem | null
  maxRows: number
  // See LabRowLine — the capability-voice hierarchy's sans body face.
  sansBody?: boolean
  // Custom row-name renderer (the frontier split's "region-tags" design
  // suffixes each name with a small geography tag). Unset renders lab.name.
  renderName?: (lab: PowerLabData) => ReactNode
  // Boxes every row (the frontier split's "by-country-row-outline" design) —
  // see LabRowBox. The "And N more" line stays unboxed.
  rowBox?: LabRowBox
  // A small outline box before every row's name (the frontier split's
  // "by-country-row-marker" design) — see LabRowLeadingBox. The "And N more"
  // line stays unmarked.
  rowLeadingBox?: LabRowLeadingBox
}) => {
  // Only truncate when it actually saves a row — capping 8 labs at 7 rows
  // would swap one lab for "And 1 more", which reads worse than the lab.
  const overflows = labs.length > maxRows + 1
  const shown = overflows ? labs.slice(0, maxRows) : labs
  const hiddenCount = labs.length - shown.length
  // When the hovered lab is one of the collapsed rows, the "And N more" line
  // takes the red-bold hover treatment in the lab row's place.
  const hoveredLabHidden =
    hoveredItem?.kind === "lab" &&
    labs.slice(shown.length).some(lab => lab.id === hoveredItem.id)
  return (
    <>
      {shown.map((lab, index) => (
        <LabRowLine
          key={lab.id}
          name={renderName ? renderName(lab) : lab.name}
          compute={lab.compute}
          highlighted={hoveredItem?.kind === "lab" && hoveredItem.id === lab.id}
          sansBody={sansBody}
          box={rowBox}
          leadingBox={rowLeadingBox}
          isFirstRow={index === 0}
        />
      ))}
      {hiddenCount > 0 && (
        <div
          className={`${hoveredLabHidden ? "" : "opacity-60"} ${sansBody ? "font-sans" : "font-mono"}`}
          style={{
            fontSize: sansBody
              ? POWER_BREAKDOWN_HIERARCHY_STYLE.rowFontSize
              : POWER_HOVER_STYLE.rowFontSize,
            lineHeight: POWER_HOVER_STYLE.rowLineHeight,
            fontWeight: hoveredLabHidden ? 700 : undefined,
            color: hoveredLabHidden ? POWER_HOVER_STYLE.hoveredColor : undefined,
          }}
        >
          {POWER_COMBO_MORE_LABEL(hiddenCount)}
        </div>
      )}
    </>
  )
}

// One region's group inside a combo column: header with total, then the
// capped lab list. Stock treatment: dimmed as a unit when another region is
// hovered. With an `emphasis` set (POWER_BREAKDOWN_EMPHASIS_STYLE) the
// hovered region is marked in red instead and nothing dims. Exported for the
// below-the-HUD three-column breakdown (belowHudDetailSwap.tsx).
export const RegionGroupBlock = ({
  region,
  regions,
  hoveredItem,
  emphasis,
  totalSuffix,
  itemHighlightDisabled = false,
  hierarchy = false,
  maxLabRows = POWER_COMBO_MAX_LAB_ROWS,
}: {
  region: PowerRegionData
  regions: PowerRegionData[]
  // Null renders the group with nothing highlighted or dimmed (the power-duo
  // panel's static reproduction of the hover view).
  hoveredItem: HoveredPowerItem | null
  emphasis?: PowerBreakdownRegionEmphasis
  // See RegionHeaderLine's totalSuffix (the "value-units" metric label).
  totalSuffix?: string
  // Drops the per-item highlights (red-bold lab row, red region header) —
  // the column-level emphasis alone marks the hovered group.
  itemHighlightDisabled?: boolean
  // The capability-voice hierarchy (POWER_BREAKDOWN_HIERARCHY_STYLE):
  // body-size bold headers, sans lab rows.
  hierarchy?: boolean
  // Lab-list cap before rows collapse into "And N more" — the below-HUD
  // breakdown lowers it on short viewports (useHoverDetailViewportRowCap)
  // so the list never runs off screen.
  maxLabRows?: number
}) => {
  const hoveredRegionId = hoveredItem ? getHoveredRegionId(hoveredItem, regions) : null
  const isHoveredRegion = hoveredRegionId === region.id
  const chip = emphasis === "chip" && isHoveredRegion
  // Frame/rail modes reserve their padding with a transparent border on
  // EVERY group, so columns never shift when the hover arrives or moves.
  const frameStyle =
    emphasis === "frame"
      ? {
          border: `${POWER_BREAKDOWN_EMPHASIS_STYLE.frame.borderWidthPx}px solid ${
            isHoveredRegion ? POWER_BREAKDOWN_EMPHASIS_STYLE.frame.borderColor : "transparent"
          }`,
          borderRadius: POWER_BREAKDOWN_EMPHASIS_STYLE.frame.borderRadiusPx,
          padding: POWER_BREAKDOWN_EMPHASIS_STYLE.frame.paddingPx,
          margin: -(
            POWER_BREAKDOWN_EMPHASIS_STYLE.frame.borderWidthPx +
            POWER_BREAKDOWN_EMPHASIS_STYLE.frame.paddingPx
          ),
        }
      : undefined
  const railStyle =
    emphasis === "rail"
      ? {
          borderLeft: `${POWER_BREAKDOWN_EMPHASIS_STYLE.rail.railWidthPx}px solid ${
            isHoveredRegion ? POWER_BREAKDOWN_EMPHASIS_STYLE.rail.railColor : "transparent"
          }`,
          paddingLeft: POWER_BREAKDOWN_EMPHASIS_STYLE.rail.gapPx,
          marginLeft: -(
            POWER_BREAKDOWN_EMPHASIS_STYLE.rail.railWidthPx +
            POWER_BREAKDOWN_EMPHASIS_STYLE.rail.gapPx
          ),
        }
      : undefined
  return (
    <div
      style={{
        // Red marks the hovered group in the emphasis modes — the others
        // stay full black instead of dimming.
        opacity: emphasis || !hoveredRegionId || isHoveredRegion ? 1 : 0.55,
        ...frameStyle,
        ...railStyle,
      }}
    >
      {/* the "overline" emphasis: the red rule ABOVE the header — present
          (transparent) on every column so headers never shift */}
      {emphasis === "overline" && (
        <div
          style={{
            height: POWER_BREAKDOWN_EMPHASIS_STYLE.overline.ruleWidthPx,
            marginBottom: POWER_BREAKDOWN_EMPHASIS_STYLE.overline.gapPx,
            background: isHoveredRegion
              ? POWER_BREAKDOWN_EMPHASIS_STYLE.overline.ruleColor
              : "transparent",
            transition: "background 160ms ease",
          }}
        />
      )}
      <div
        style={
          chip
            ? {
                background: POWER_BREAKDOWN_EMPHASIS_STYLE.chip.background,
                padding: POWER_BREAKDOWN_EMPHASIS_STYLE.chip.padding,
                margin: POWER_BREAKDOWN_EMPHASIS_STYLE.chip.margin,
                borderRadius: POWER_BREAKDOWN_EMPHASIS_STYLE.chip.borderRadiusPx,
              }
            : undefined
        }
      >
        <RegionHeaderLine
          region={region}
          highlighted={
            !itemHighlightDisabled &&
            hoveredItem?.kind === "region" &&
            hoveredItem.id === region.id
          }
          colorOverride={
            chip
              ? POWER_BREAKDOWN_EMPHASIS_STYLE.chip.textColor
              : emphasis === "rail" && isHoveredRegion
                ? POWER_BREAKDOWN_EMPHASIS_STYLE.rail.headerColor
                : undefined
          }
          leadingMarker={
            emphasis === "marker" ? { visible: isHoveredRegion } : undefined
          }
          totalSuffix={totalSuffix}
          hierarchy={hierarchy}
        />
      </div>
      {/* the "underline" emphasis: a red rule under the header, full column
          width — present (transparent) on every column so rows never shift */}
      {emphasis === "underline" && (
        <div
          style={{
            height: POWER_BREAKDOWN_EMPHASIS_STYLE.underline.ruleWidthPx,
            marginTop: POWER_BREAKDOWN_EMPHASIS_STYLE.underline.gapPx,
            background: isHoveredRegion
              ? POWER_BREAKDOWN_EMPHASIS_STYLE.underline.ruleColor
              : "transparent",
            transition: "background 160ms ease",
          }}
        />
      )}
      <div className="mt-0.5">
        <CappedLabList
          labs={getLabsWorthShowing(region)}
          hoveredItem={itemHighlightDisabled ? null : hoveredItem}
          maxRows={maxLabRows}
          sansBody={hierarchy}
        />
      </div>
    </div>
  )
}

// The left half of the combo layout (from HUD 107): America and China side by
// side as two full columns (header with total, then every lab with its
// compute, capped per POWER_COMBO_MAX_LAB_ROWS). Rest of World stacks in the
// SAME column as China — until that column would exceed
// POWER_COMBO_STACKED_MAX_ROWS, at which point RoW scoots over to the right
// side under the dist-of-power section instead (the paired
// PowerHoverWorldBelowTreemap with onlyWhenWorldScootsRight renders it there).
const PowerHoverUsChinaWorldColumns = ({
  regions,
  hoveredItem,
  // The panel reproduction has no below-treemap slot for Rest of World to
  // scoot into, so it always stacks under China regardless of overflow.
  alwaysStackWorld = false,
  // See RegionGroupBlock — the capability-voice hierarchy (sans lab rows).
  hierarchy = false,
}: {
  regions: PowerRegionData[]
  hoveredItem: HoveredPowerItem | null
  alwaysStackWorld?: boolean
  hierarchy?: boolean
}) => {
  const america = regions.find((r) => r.id === "america")
  const china = regions.find((r) => r.id === "china")
  const world = regions.find((r) => r.id === "world")
  const stackWorldUnderChina = world && (alwaysStackWorld || !shouldWorldScootRight(regions))
  return (
    <div className="flex gap-3">
      <div className="flex-1 min-w-0">
        {america && (
          <RegionGroupBlock region={america} regions={regions} hoveredItem={hoveredItem} hierarchy={hierarchy} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        {china && (
          <RegionGroupBlock region={china} regions={regions} hoveredItem={hoveredItem} hierarchy={hierarchy} />
        )}
        {stackWorldUnderChina && (
          <div className="mt-1.5">
            <RegionGroupBlock region={world} regions={regions} hoveredItem={hoveredItem} hierarchy={hierarchy} />
          </div>
        )}
      </div>
    </div>
  )
}

// The combo columns, self-contained on live data — the power-duo panel's
// reproduction of the hover view (nothing highlighted, Rest of World always
// stacked under China). Always the capability-voice hierarchy (sans lab
// rows) — the details panels must show the same face the hover breakdown
// does (2026-07-07: the panels were falling back to mono because they sit
// outside the hover provider).
export const PowerRegionsCombo = ({
  hoveredItem = null,
  className,
}: {
  hoveredItem?: HoveredPowerItem | null
  className?: string
}) => {
  const regions = usePowerRegions()
  return (
    <div className={className}>
      <PowerHoverUsChinaWorldColumns
        regions={regions}
        hoveredItem={hoveredItem}
        alwaysStackWorld
        hierarchy
      />
    </div>
  )
}

// The other half of the combo layout: while the treemap is hovered, Rest of
// World's list appears UNDER the dist-of-power section, its labs flowing
// across two columns. Absolutely positioned (topPx below the slot's top)
// inside a relative wrapper, over the space the hidden labor viz frees up —
// the canvas never reflows.
export const PowerHoverWorldBelowTreemap = ({
  topPx,
  // Render only when the China column got too big to also hold Rest of World
  // (see shouldWorldScootRight) — otherwise the left overlay shows it.
  onlyWhenWorldScootsRight = false,
}: {
  topPx: number
  onlyWhenWorldScootsRight?: boolean
}) => {
  const swap = usePowerHoverSwap()
  const regions = usePowerRegions()
  const hoveredItem = swap?.hoveredItem ?? null
  const region = regions.find((r) => r.id === "world")
  const active = Boolean(
    hoveredItem &&
      region &&
      (!onlyWhenWorldScootsRight || shouldWorldScootRight(regions)),
  )
  const hoveredRegionId = hoveredItem ? getHoveredRegionId(hoveredItem, regions) : null
  return (
    <HoverSwapFadeOverlay
      active={active}
      className="absolute left-0 right-0 z-10"
      style={{ top: topPx }}
    >
      {hoveredItem && region && (
        // Dim only when some OTHER region is hovered — the whole-chart "chart"
        // hover (no region resolved) shows everything at full strength, same as
        // RegionGroupBlock.
        <div
          style={{
            opacity: hoveredRegionId && region.id !== hoveredRegionId ? 0.55 : 1,
          }}
        >
          <RegionHeaderLine
            region={region}
            highlighted={hoveredItem.kind === "region" && hoveredItem.id === region.id}
          />
          <div className="mt-0.5" style={{ columnCount: 2, columnGap: 12 }}>
            <CappedLabList
              labs={getLabsWorthShowing(region)}
              hoveredItem={hoveredItem}
              maxRows={POWER_COMBO_MAX_LAB_ROWS * 2}
            />
          </div>
        </div>
      )}
    </HoverSwapFadeOverlay>
  )
}

// Hides its children (keeping their layout footprint) while the treemap is
// hovered — wraps the labor viz so the Rest-of-World block and any list
// overflow land on clean canvas. With onlyWhenWorldScootsRight the children
// stay visible through hover and only hide once the overflow actually sends
// Rest of World over to the right side, where it needs the space below the
// treemap.
export const PowerHoverHideWhileHovered = ({
  className,
  children,
  onlyWhenWorldScootsRight = false,
}: {
  className?: string
  children: ReactNode
  onlyWhenWorldScootsRight?: boolean
}) => {
  const swap = usePowerHoverSwap()
  const regions = usePowerRegions()
  const hidden =
    Boolean(swap?.hoveredItem) &&
    (!onlyWhenWorldScootsRight || shouldWorldScootRight(regions))
  const sequentialFade = useHoverSwapSequentialFade()
  return (
    <div
      className={className}
      style={hoverSwapHiddenAndUntargetableContentStyle(hidden, sequentialFade)}
    >
      {children}
    </div>
  )
}

// The full three-region breakdown with nothing highlighted — live data (it
// re-renders as the story year moves), used by the metric-details panel
// redesigns as the "additional detailed data" under the Distribution of Power
// header. Always the capability-voice hierarchy (sans lab rows) — the panels
// must show the same face the hover breakdown does (2026-07-07: they were
// falling back to mono because they sit outside the hover provider).
export const PowerRegionsLiveBreakdown = ({ className }: { className?: string }) => {
  const regions = usePowerRegions()
  return (
    <div className={`flex gap-3 ${className ?? ""}`}>
      {regions.map((region) => (
        <div key={region.id} className="flex-1 min-w-0">
          <RegionHeaderLine region={region} highlighted={false} hierarchy />
          <div className="mt-0.5">
            <CappedLabList
              labs={getLabsWorthShowing(region)}
              hoveredItem={null}
              maxRows={POWER_COMBO_MAX_LAB_ROWS}
              sansBody
            />
          </div>
        </div>
      ))}
    </div>
  )
}

// Treatments for the "Click for details" affordance (one per tab 13–16):
//   "title-inline"   — label + red ↗ riding the title line's right edge
//   "badge"          — a bordered chip with a click icon under the title
//   "top-strip"      — a micro-label with red icon ABOVE the title
//   "breakout-above" — floats ABOVE the treemap itself, right-aligned and
//                      overshooting the box into the page margin (rendered by
//                      PowerHoverClickHintAboveTreemap, not the left overlay)
export type PowerHoverClickHint = "title-inline" | "badge" | "top-strip" | "breakout-above"

// How far the breakout hint overshoots the treemap's right edge into the
// margin (0 = right edges flush), and how far above the box it floats.
export const POWER_HOVER_BREAKOUT_HINT_RIGHT_OVERSHOOT_PX = 0
export const POWER_HOVER_BREAKOUT_HINT_ABOVE_PX = 20

// The "breakout-above" hint (tab 16): mount as a CHILD of
// <HudPersistentGlobeAndGraph> (like CapabilityHoverBridge) — NOT inside the
// lower HUD body, whose AnimateYear wrapper is overflow-hidden and clips
// anything poking above its top edge. The graph container's flow ends right
// at the gap above the treemap, so a zero-height anchor there puts the hint
// in that gap: right-aligned to the HUD's right edge (= the treemap's right
// edge) and overshooting into the page margin. Requires the PowerHoverSwap
// provider to wrap the graph block too, so the hover state is visible here.
// Absolutely positioned + zero-height anchor: nothing on the HUD moves when
// it appears.
export const PowerHoverClickHintAboveTreemap = () => {
  const swap = usePowerHoverSwap()
  return (
    <div className="relative h-0" aria-hidden>
      <HoverSwapFadeOverlay
        active={Boolean(swap?.hoveredItem)}
        className="pointer-events-none absolute z-20"
        style={{
          bottom: POWER_HOVER_BREAKOUT_HINT_ABOVE_PX - 16,
          right: -POWER_HOVER_BREAKOUT_HINT_RIGHT_OVERSHOOT_PX,
        }}
      >
        {/* Layout lives INSIDE the overlay (its root is a positioning-only
            grid — see HoverSwapFadeOverlay). */}
        <div className="flex items-center gap-1 whitespace-nowrap font-mono text-[9px] text-black">
          {POWER_HOVER_CLICK_HINT_LABEL}
          <ArrowUpRight size={10} strokeWidth={2.2} style={{ color: "var(--accent)" }} />
        </div>
      </HoverSwapFadeOverlay>
    </div>
  )
}

// Wraps the HUD block(s) whose spot the distribution-of-power info takes over
// while the treemap is hovered. Children keep their layout footprint but go
// visibility-hidden; the overlay paints over them and may run past the slot's
// bottom edge (never reflowing the canvas). Shows America and China (plus
// Rest of World when it fits) as full columns; further overlay modes
// (region-focus, stat-line, full-list, …) live in the aesthetic-options
// archive.
export const PowerHoverSwapTarget = ({
  className,
  children,
  clickHint,
}: {
  className?: string
  children: ReactNode
  clickHint?: PowerHoverClickHint
}) => {
  const swap = usePowerHoverSwap()
  const regions = usePowerRegions()
  const sequentialFade = useHoverSwapSequentialFade()
  const hoveredItem = swap?.hoveredItem ?? null
  return (
    // Entering this column dismisses the takeover, putting the children
    // (policies, stat row) back under the pointer. The enclosing
    // HoverSwapStickyZone holds the takeover across the gap between the
    // columns; this is the arrival-side handoff.
    <div
      className={`relative ${className ?? ""}`}
      onMouseOver={hoveredItem ? () => swap?.setHoveredItem(null) : undefined}
    >
      {/* Hidden via opacity (not visibility) so the children stay
          HIT-TESTABLE while covered: a pointer sweeping in from the treemap
          lands directly on a policy square / metric and swaps takeover →
          detail in ONE event, with no default-state frame between. Undefined
          when idle so an outer swap target's hiding still inherits. */}
      <div style={hoverSwapHiddenContentStyle(Boolean(hoveredItem), sequentialFade)}>
        {children}
      </div>
      {/* pointer-events-none: the overlay never intercepts the pointer, so
          the (invisible) content underneath handles the arrival hover. */}
      <HoverSwapFadeOverlay
        active={Boolean(hoveredItem)}
        className="absolute top-0 left-0 right-0 z-10 pointer-events-none"
      >
        {hoveredItem && (
          <>
          {/* Palette per design-style-guide.md: black text only — the hint is
              de-emphasized by SIZE, not opacity or gray — with red confined
              to the small icon so it signals without competing with content. */}
          {clickHint === "top-strip" && (
            <div className="mb-1 flex items-center gap-1.5 font-mono text-[8.5px] font-bold uppercase tracking-wider text-black">
              <MousePointerClick size={10} strokeWidth={2.2} style={{ color: "var(--accent)" }} />
              {POWER_HOVER_CLICK_HINT_LABEL}
            </div>
          )}
          <div className="flex items-baseline justify-between gap-2">
            <div
              className="font-mono font-bold tracking-[0.04em]"
              style={{ fontSize: POWER_HOVER_TITLE_FONT_SIZE }}
            >
              {POWER_HOVER_TITLE}
            </div>
            {clickHint === "title-inline" && (
              <span className="flex shrink-0 items-center gap-1 self-center font-mono text-[9px] text-black">
                {POWER_HOVER_CLICK_HINT_LABEL}
                <ArrowUpRight size={10} strokeWidth={2.2} style={{ color: "var(--accent)" }} />
              </span>
            )}
          </div>
          {clickHint === "badge" && (
            <span className="mt-1 inline-flex items-center gap-1 rounded-full border border-black px-2 py-[2px] font-mono text-[8.5px] text-black">
              <MousePointerClick size={9} strokeWidth={2.2} style={{ color: "var(--accent)" }} />
              {POWER_HOVER_CLICK_HINT_LABEL}
            </span>
          )}
          {/* data-hud-power-breakdown: takeoff element for the flight into
              the power-duo panel, which leads with this same combo view. */}
          <div data-hud-power-breakdown className="mt-1.5">
            <PowerHoverUsChinaWorldColumns regions={regions} hoveredItem={hoveredItem} />
          </div>
          </>
        )}
      </HoverSwapFadeOverlay>
    </div>
  )
}
